import React, { useState, useEffect } from 'react';
import { Target, Flame, TrendingUp, Check, Clock, ExternalLink } from 'lucide-react';

const PartnerHabitsSummary = ({ partnerId, partnerName }) => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (partnerId) {
      loadPartnerSummary();
    }
  }, [partnerId]);

  const loadPartnerSummary = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${BACKEND_URL}/api/member/habits/partner/${partnerId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSummary(data);
      } else if (response.status === 403) {
        // No active partnership
        setSummary(null);
      }
    } catch (error) {
      console.error('Error loading partner summary:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-gradient-to-br from-blue-50 to-blue-100 border-2 border-blue-200 rounded-lg p-6">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (!summary) {
    return (
      <div className="bg-gradient-to-br from-gray-50 to-gray-100 border-2 border-gray-200 rounded-lg p-6">
        <div className="text-center">
          <Target className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600">Partner hasn't started tracking habits yet</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-blue-50 to-blue-100 border-2 border-blue-200 rounded-lg p-6 shadow-sm">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-xl font-bold text-blue-900 flex items-center gap-2">
            <Target className="w-6 h-6" />
            {partnerName}'s Habit Progress
          </h3>
          <p className="text-blue-700 text-sm mt-1">Weekly activity summary</p>
        </div>
        <button
          onClick={() => window.location.href = '#'}
          className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center gap-1"
        >
          View Full Habits
          <ExternalLink className="w-4 h-4" />
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {/* Active Habits */}
        <div className="bg-white rounded-lg p-4 text-center border border-blue-200">
          <div className="flex items-center justify-center mb-2">
            <Target className="w-6 h-6 text-blue-600" />
          </div>
          <p className="text-2xl font-bold text-blue-900">{summary.active_habits}</p>
          <p className="text-xs text-blue-600 mt-1">Active Habits</p>
        </div>

        {/* Completion Rate */}
        <div className="bg-white rounded-lg p-4 text-center border border-blue-200">
          <div className="flex items-center justify-center mb-2">
            <Check className="w-6 h-6 text-green-600" />
          </div>
          <p className="text-2xl font-bold text-green-900">{summary.completion_rate}%</p>
          <p className="text-xs text-green-600 mt-1">Completion Rate</p>
        </div>

        {/* Best Streak */}
        <div className="bg-white rounded-lg p-4 text-center border border-blue-200">
          <div className="flex items-center justify-center mb-2">
            <Flame className="w-6 h-6 text-orange-600" />
          </div>
          <p className="text-2xl font-bold text-orange-900">{summary.best_streak}</p>
          <p className="text-xs text-orange-600 mt-1">Best Streak</p>
        </div>

        {/* This Week */}
        <div className="bg-white rounded-lg p-4 text-center border border-blue-200">
          <div className="flex items-center justify-center mb-2">
            <TrendingUp className="w-6 h-6 text-purple-600" />
          </div>
          <p className="text-2xl font-bold text-purple-900">{summary.week_completions}</p>
          <p className="text-xs text-purple-600 mt-1">This Week</p>
        </div>
      </div>

      {/* Motivational Message */}
      {summary.completion_rate >= 70 && (
        <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-3">
          <p className="text-sm text-green-800 text-center">
            🎉 {partnerName} is crushing it this week! Send them some encouragement!
          </p>
        </div>
      )}

      {summary.completion_rate < 40 && summary.active_habits > 0 && (
        <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-3">
          <p className="text-sm text-amber-800 text-center">
            💪 Reach out to {partnerName} - they might need some support!
          </p>
        </div>
      )}

      {summary.best_streak >= 7 && (
        <div className="mt-4 bg-orange-50 border border-orange-200 rounded-lg p-3">
          <p className="text-sm text-orange-800 text-center">
            🔥 {partnerName} has a {summary.best_streak}-day streak! That's impressive consistency!
          </p>
        </div>
      )}
    </div>
  );
};

export default PartnerHabitsSummary;
